<script setup>
import {ref, onMounted, computed, getCurrentInstance, watch} from 'vue'
    import { useRouter, useRoute } from 'vue-router'
    import insightsNewsletterService from "../../services/insights/insightsNewsletterService";
    import {usePageLoadingStore} from "../../../../stores/pageLoading";
    import {useUserInsightsStore} from "../../stores/insights/userInsightsStore";

    const loadingStore = usePageLoadingStore()
    const userInsightsStore = useUserInsightsStore()
    const { $pageListBoardNo } = getCurrentInstance().appContext.config.globalProperties;
    const router = useRouter();
    const route = useRoute();

    const viewType = ref("gallery");

    const searchType = ref('all');
    const searchTypeOptions = [
        { label:'전체', value:'all', },
        { label:'제목', value:'title', },
        { label:'내용', value:'content', },
    ]
    const input = ref({
        search: '',
    })

    const list = ref([])
    const currentPage = ref(1);
    const pageSize = computed(() => viewType.value === 'gallery' ? 12 : 10);
    const totalPages = ref(1);
    const totalCount = ref(0);

    onMounted(()=>{
        const q = route.query || {};
        if (q.viewType && (q.viewType === 'gallery' || q.viewType === 'list')) {
            viewType.value = q.viewType;
        }
        if (q.page) {
            const p = Number(q.page);
            currentPage.value = Number.isNaN(p) ? 1 : p;
        }
        if (q.searchType && ['all','title','content'].includes(q.searchType)) {
            searchType.value = q.searchType;
        }
        if (q.search) {
            input.value.search = String(q.search);
        }
        retrieveList();
    });

    watch(viewType, () => {
      const hasPageInQuery = !!route.query.page;
      if (!hasPageInQuery) {
        currentPage.value = 1;
      }
      const url = new URL(window.location.href);
      url.searchParams.set('viewType', viewType.value);
      url.searchParams.set('page', String(currentPage.value));
      url.searchParams.set('searchType', searchType.value);
      url.searchParams.set('search', input.value.search || '');
      window.history.replaceState({}, '', url.toString());
      retrieveList();
    });

    const isInitView = computed(()=> {
        return currentPage.value === 1 && searchType.value === 'all' && input.value.search === '';
    });

    const retrieveList = () => {
        let paramObj = {
            pageNo: currentPage.value,
            size: pageSize.value,
            searchType: searchType.value,
            searchInput: input.value.search || ""
        }
        loadingStore.loading = true;
        insightsNewsletterService.retrieveAllPaging(paramObj)
            .then( (res) => {
                loadingStore.loading = false;
                if(res.data.code.toLowerCase() === 'success') {
                    const result = res.data.result;
                    totalPages.value = result.totalPages;
                    totalCount.value = result.totalElements;
                    list.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
                    if(isInitView.value) {
                        userInsightsStore.setNewsletterList(list.value);
                    }
                }
            }).catch( (error) =>  {
            loadingStore.loading = false;
            console.log(error);
        });
    }

    const onSelectedPage = (pageNo) => {
        currentPage.value = pageNo;
        const url = new URL(window.location.href);
        url.searchParams.set('viewType', viewType.value);
        url.searchParams.set('page', String(currentPage.value));
        url.searchParams.set('searchType', searchType.value);
        url.searchParams.set('search', input.value.search || '');
        window.history.replaceState({}, '', url.toString());
        retrieveList();
    }

    const onSelectedNewsletter = (newsletter) => {
        router.push({ name: 'InsightsNewsletterDetail', params: {id: newsletter.newsletterId }, query: { viewType: viewType.value, page: currentPage.value, searchType: searchType.value, search: input.value.search || '' } });
    }
</script>

<template>
    <div class="container">
        <div class="inner">
            <div class="title page-title">
                <h3>뉴스레터</h3>
            </div>
            <div class="list-top">
                <p>총 게시물 <span>{{ $number(totalCount) }}</span>건</p>
                <div class="search-box">
                  <ul class="view-toggle">
                    <li @click="viewType = 'gallery'" :class="{active: viewType === 'gallery'}">
                      <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="6" y="6" width="11" height="11" rx="3" fill="#E4E5F0"/>
                        <rect x="19" y="6" width="11" height="11" rx="3" fill="#E4E5F0"/>
                        <rect x="6" y="19" width="11" height="11" rx="3" fill="#E4E5F0"/>
                        <rect x="19" y="19" width="11" height="11" rx="3" fill="#E4E5F0"/>
                      </svg>
                    </li>
                    <li @click="viewType = 'list'" :class="{active: viewType === 'list'}">
                      <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="6" y="24" width="17" height="6" rx="2" fill="#E4E5F0"/>
                        <rect x="24" y="24" width="6" height="6" rx="3" fill="#E4E5F0"/>
                        <rect x="6" y="15" width="17" height="6" rx="2" fill="#E4E5F0"/>
                        <rect x="24" y="15" width="6" height="6" rx="3" fill="#E4E5F0"/>
                        <rect x="6" y="6" width="17" height="6" rx="2" fill="#E4E5F0"/>
                        <rect x="24" y="6" width="6" height="6" rx="3" fill="#E4E5F0"/>
                      </svg>
                    </li>
                  </ul>
                </div>
            </div>
            <div v-if="viewType === 'gallery'" class="newsletter-wrap">
              <div v-for="(newsletter, index) in (isInitView ? userInsightsStore.newsletterList : list)" class="newsletter-item" @click="onSelectedNewsletter(newsletter)">
                <div class="img">
                    <KSAImage v-if="newsletter.newsletterThumbDir" :src="newsletter.newsletterThumbDir" />
                    <div v-else class="no-image"><p>이미지 없음</p></div>
                </div>
                <div class="txt">
                    <h4>{{ newsletter.title }} <img v-if="newsletter.attachList.length > 0 && newsletter.attachList[0].fileName" src="/src/assets/images/icon-file.png" class="icon-file" /></h4>
                    <p>{{ $yyyymmddOrHHmm(newsletter.createDate)}}</p>
                </div>
              </div>
            </div>
            <div v-else class="table-form board-list border-list-mobile">
                <table>
                    <colgroup>
                        <col width="5%" />
                        <col />
                        <col width="15%" />
                        <col width="5%"/>
                    </colgroup>
                    <thead>
                        <tr>
                            <th>번호</th>
                            <th>제목</th>
                            <th>작성일</th>
                            <th>조회수</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(newsletter, index) in (isInitView ? userInsightsStore.newsletterList : list)">
                            <td data-title="번호 : ">{{ newsletter.boardNo }}</td>
                            <td class="text-left mobile-list-main" data-title="제목 : " style="cursor:pointer;">
                                <p class="text-over" @click="onSelectedNewsletter(newsletter)">{{ newsletter.title }} <img v-if="newsletter.attachList.length > 0 && newsletter.attachList[0].fileName" src="/src/assets/images/icon-file.png" class="icon-file" /></p>
                            </td>
                            <td class="mobile-list-sub" data-title="작성일 : ">{{ $yyyymmddOrHHmm(newsletter.createDate)}}</td>
                            <td class="mobile-list-sub" data-title="조회수 : ">{{ $number(newsletter.hitCount) }}</td>
                        </tr>
                    </tbody>
                </table>
                <div class="result-none" v-if="list.length === 0">
                    검색 결과가 없습니다.
                </div>
            </div>
            <Paginate
                v-if="list.length > 0"
                v-model="currentPage"
                :page-count="totalPages"
                :click-handler="onSelectedPage"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
    table {
      table-layout: fixed;
    }
    @media (max-width: 767px) {
        table {
            colgroup {
               display: none;
            }
        }
    }
</style>